'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBuilderStore } from '@/store/builderStore';
import { Sparkles, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import Image from 'next/image';
import { clsx } from 'clsx';

export default function AIPreviewPanel() {
  const { generatedImages, selectedImageIndex, setSelectedImageIndex, currentStep, product } =
    useBuilderStore();

  const [carouselIndex, setCarouselIndex] = useState(0);

  const hasImages = generatedImages.length > 0;

  const nextImage = () => {
    const newIdx = (carouselIndex + 1) % generatedImages.length;
    setCarouselIndex(newIdx);
  };

  const prevImage = () => {
    const newIdx = (carouselIndex - 1 + generatedImages.length) % generatedImages.length;
    setCarouselIndex(newIdx);
  };

  const handleSelect = (idx: number) => {
    setSelectedImageIndex(idx);
    setCarouselIndex(idx);
  };

  return (
    <aside className="w-80 shrink-0 hidden xl:flex flex-col gap-4">
      <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden sticky top-24">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-5 py-4 flex items-center gap-2">
          <Sparkles size={16} className="text-amber-400" />
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">
            AI Generated Preview
          </h3>
        </div>

        <div className="p-4">
          {hasImages ? (
            <div className="space-y-4">
              {/* Main image carousel */}
              <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-slate-50 border border-slate-100">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={carouselIndex}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0"
                  >
                    <Image
                      src={generatedImages[carouselIndex]}
                      alt={`Generated product ${carouselIndex + 1}`}
                      fill
                      className="object-contain"
                      unoptimized
                    />
                  </motion.div>
                </AnimatePresence>

                {/* Selected badge */}
                {carouselIndex === selectedImageIndex && (
                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow">
                    <Check size={10} strokeWidth={3} />
                    Selected
                  </div>
                )}

                {/* Nav arrows */}
                {generatedImages.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white rounded-full shadow-md flex items-center justify-center transition-all hover:scale-110 border border-slate-100"
                    >
                      <ChevronLeft size={16} className="text-slate-700" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white rounded-full shadow-md flex items-center justify-center transition-all hover:scale-110 border border-slate-100"
                    >
                      <ChevronRight size={16} className="text-slate-700" />
                    </button>
                  </>
                )}
              </div>

              {/* Dot indicators */}
              <div className="flex justify-center gap-2">
                {generatedImages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCarouselIndex(idx)}
                    className={clsx(
                      'w-2 h-2 rounded-full transition-all duration-200',
                      carouselIndex === idx
                        ? 'bg-amber-500 scale-125'
                        : 'bg-slate-300 hover:bg-slate-400'
                    )}
                  />
                ))}
              </div>

              {/* Thumbnail grid */}
              <div className="grid grid-cols-4 gap-2">
                {generatedImages.map((img, idx) => (
                  <motion.button
                    key={idx}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleSelect(idx)}
                    className={clsx(
                      'relative aspect-square rounded-lg overflow-hidden border-2 transition-all duration-200',
                      {
                        'border-amber-500 shadow-md shadow-amber-200': selectedImageIndex === idx,
                        'border-slate-200 hover:border-amber-300': selectedImageIndex !== idx,
                      }
                    )}
                  >
                    <Image
                      src={img}
                      alt={`Option ${idx + 1}`}
                      fill
                      className="object-cover"
                      unoptimized
                    />
                    {selectedImageIndex === idx && (
                      <div className="absolute inset-0 bg-amber-500/10 flex items-center justify-center">
                        <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center shadow">
                          <Check size={10} strokeWidth={3} className="text-white" />
                        </div>
                      </div>
                    )}
                  </motion.button>
                ))}
              </div>

              <p className="text-xs text-center text-slate-400">
                Image {carouselIndex + 1} of {generatedImages.length} · Click to select
              </p>
            </div>
          ) : (
            <div className="aspect-square rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-100 flex flex-col items-center justify-center gap-4 text-center p-6">
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-slate-200/80 flex items-center justify-center">
                  <Sparkles size={28} className="text-slate-400" />
                </div>
                {/* Decorative rings */}
                <div className="absolute inset-0 rounded-full border-2 border-dashed border-slate-200 scale-125 animate-spin-slow" />
              </div>

              <div>
                <p className="text-sm font-bold text-slate-600 mb-1">No Preview Yet</p>
                <p className="text-xs text-slate-400 leading-relaxed">
                  {currentStep < 4
                    ? `Complete step ${currentStep} and reach AI Preview to generate images`
                    : product.productName
                    ? `Click "Generate AI Preview" to create images of your ${product.productName}`
                    : 'Fill in product details to generate AI previews'}
                </p>
              </div>

              {/* Placeholder skeleton cards */}
              <div className="grid grid-cols-2 gap-2 w-full mt-2">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-lg bg-slate-200/60 animate-pulse"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Step indicator */}
        <div className="px-5 pb-4">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>Step {currentStep} of 5</span>
            <span className="font-semibold text-amber-600">
              {Math.round(((currentStep - 1) / 4) * 100)}% complete
            </span>
          </div>
          <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: `${((currentStep - 1) / 4) * 100}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
        </div>
      </div>
    </aside>
  );
}
